/**
 * FloatingPanel - Compact quick-edit panels for the floating toolbar
 * 
 * Panel types:
 * - text: Text edit + typography token pickers
 * - design: Background/border/radius/shadow token pickers
 * - animate: Entrance animation controls
 * - image: Image URL/upload
 * - link: Button destination
 * - content: Card content editor
 * 
 * Reuses token pickers from TokenPicker.js
 */
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/react';
import { 
  X, Type, Palette, Sparkles, Image, Link2, 
  AlignLeft, AlignCenter, AlignRight, Play, RotateCcw
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Slider } from '../../ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  ColorTokenPicker,
  FontFamilyPicker,
  FontSizePicker,
  FontWeightPicker,
  RadiusPicker,
  ShadowPicker,
  SpacingPicker
} from '../TokenPicker';
import { getElementValue, getElementStyles, getElementMotion } from '../ElementRegistry';

// Text alignment options
const ALIGNMENTS = [
  { value: 'left', icon: AlignLeft, label: 'Left' },
  { value: 'center', icon: AlignCenter, label: 'Center' },
  { value: 'right', icon: AlignRight, label: 'Right' }
];

// Motion easing presets
const EASING_OPTIONS = [
  { value: 'smooth', label: 'Smooth' },
  { value: 'calm', label: 'Calm' },
  { value: 'snappy', label: 'Snappy' }
];

// Panel header with close button
const PanelHeader = ({ title, icon: Icon, onClose }) => (
  <div className="flex items-center justify-between pb-3 border-b border-zinc-800 mb-3">
    <div className="flex items-center gap-2">
      {Icon && <Icon className="w-4 h-4 text-orange-400" />}
      <span className="text-sm font-medium text-white">{title}</span>
    </div>
    <button
      onClick={onClose}
      className="p-1 rounded hover:bg-zinc-800 text-zinc-500 hover:text-white transition-colors"
    >
      <X className="w-4 h-4" />
    </button>
  </div>
);

// Text Panel - Edit text + typography
const TextPanel = ({ blockProps, tokens, onUpdateValue, onUpdateStyles, elementId, onClose }) => {
  const currentValue = getElementValue(blockProps, { id: elementId, bindPath: elementId.split('.').slice(1).join('.') });
  const currentStyles = getElementStyles(blockProps, { id: elementId, stylePath: `styles.${elementId.split('.')[1]}` }) || {};
  
  // Handle text value - could be string or object
  const textValue = typeof currentValue === 'object' 
    ? (currentValue.label || currentValue.title || currentValue.text || '') 
    : (currentValue || '');
  
  const [localText, setLocalText] = useState(textValue);
  
  const handleTextChange = useCallback((newText) => {
    setLocalText(newText);
    // Debounce the actual update
    const timeout = setTimeout(() => {
      if (typeof currentValue === 'object') {
        onUpdateValue?.({ ...currentValue, label: newText, title: newText });
      } else {
        onUpdateValue?.(newText);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [currentValue, onUpdateValue]);

  const handleStyleChange = (key, value) => {
    onUpdateStyles?.({ ...currentStyles, [key]: value });
  };

  return (
    <div className="space-y-3">
      <PanelHeader title="Edit Text" icon={Type} onClose={onClose} />
      
      {/* Text Input */}
      <div>
        <Label className="text-zinc-400 text-xs mb-1.5 block">Text Content</Label>
        <Textarea
          value={localText}
          onChange={(e) => handleTextChange(e.target.value)}
          className="bg-zinc-800 border-zinc-700 text-white text-sm min-h-[60px] resize-none"
          placeholder="Enter text..."
        />
      </div>
      
      {/* Typography Controls */}
      <div className="grid grid-cols-2 gap-2">
        <FontFamilyPicker
          label="Font"
          value={currentStyles.fontFamily}
          tokens={tokens}
          onChange={(val) => handleStyleChange('fontFamily', val)}
        />
        <FontSizePicker
          label="Size"
          value={currentStyles.fontSize}
          tokens={tokens}
          onChange={(val) => handleStyleChange('fontSize', val)}
        />
      </div>
      
      <div className="grid grid-cols-2 gap-2">
        <FontWeightPicker
          label="Weight"
          value={currentStyles.fontWeight}
          tokens={tokens}
          onChange={(val) => handleStyleChange('fontWeight', val)}
        />
        <ColorTokenPicker
          label="Color"
          value={currentStyles.color}
          tokens={tokens}
          onChange={(val) => handleStyleChange('color', val)}
        />
      </div>
      
      {/* Alignment */}
      <div>
        <Label className="text-zinc-400 text-xs mb-1.5 block">Alignment</Label>
        <div className="flex gap-1 bg-zinc-800/50 p-1 rounded-lg">
          {ALIGNMENTS.map((align) => (
            <button
              key={align.value}
              onClick={() => handleStyleChange('textAlign', align.value)}
              className={`flex-1 flex items-center justify-center p-2 rounded-md transition-colors ${
                currentStyles.textAlign === align.value
                  ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50'
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-700'
              }`}
            >
              <align.icon className="w-4 h-4" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Design Panel - Visual styling with tokens
const DesignPanel = ({ blockProps, tokens, onUpdateStyles, elementId, onClose }) => {
  const currentStyles = getElementStyles(blockProps, { id: elementId, stylePath: `styles.${elementId.split('.')[1]}` }) || {};

  const handleStyleChange = (key, value) => {
    onUpdateStyles?.({ ...currentStyles, [key]: value });
  };

  return (
    <div className="space-y-3">
      <PanelHeader title="Design" icon={Palette} onClose={onClose} />
      
      {/* Background */}
      <ColorTokenPicker
        label="Background"
        value={currentStyles.backgroundColor}
        tokens={tokens}
        onChange={(val) => handleStyleChange('backgroundColor', val)}
      />
      
      {/* Border */}
      <ColorTokenPicker
        label="Border Color"
        value={currentStyles.borderColor}
        tokens={tokens}
        onChange={(val) => handleStyleChange('borderColor', val)}
      />
      
      {/* Radius */}
      <RadiusPicker
        label="Corners"
        value={currentStyles.borderRadius}
        tokens={tokens}
        onChange={(val) => handleStyleChange('borderRadius', val)}
      />
      
      {/* Shadow */}
      <ShadowPicker
        label="Shadow"
        value={currentStyles.boxShadow}
        tokens={tokens}
        onChange={(val) => handleStyleChange('boxShadow', val)}
      />
      
      {/* Opacity */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <Label className="text-zinc-400 text-xs">Opacity</Label>
          <span className="text-zinc-500 text-xs">{Math.round((currentStyles.opacity ?? 1) * 100)}%</span>
        </div>
        <Slider
          value={[(currentStyles.opacity ?? 1) * 100]}
          onValueChange={([val]) => handleStyleChange('opacity', val / 100)}
          min={0}
          max={100}
          step={5}
          className="w-full"
        />
      </div>
      
      {/* Reset button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onUpdateStyles?.({})}
        className="w-full text-zinc-400 hover:text-white"
      >
        <RotateCcw className="w-3 h-3 mr-2" />
        Reset Styles
      </Button>
    </div>
  );
};

// Animate Panel - Motion controls
const AnimatePanel = ({ blockProps, tokens, onUpdateMotion, onReplayAnimation, elementId, onClose }) => {
  const currentMotion = getElementMotion(blockProps, { id: elementId, motionPath: `motion.${elementId.split('.')[1]}` }) || {};

  const handleMotionChange = (key, value) => {
    onUpdateMotion?.({ ...currentMotion, [key]: value });
  };

  return (
    <div className="space-y-3">
      <PanelHeader title="Animate" icon={Sparkles} onClose={onClose} />
      
      {/* Duration */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <Label className="text-zinc-400 text-xs">Duration</Label>
          <span className="text-zinc-500 text-xs">{currentMotion.durationMs || 700}ms</span>
        </div>
        <Slider
          value={[currentMotion.durationMs || 700]}
          onValueChange={([val]) => handleMotionChange('durationMs', val)}
          min={100}
          max={2000}
          step={100}
          className="w-full"
        />
      </div>
      
      {/* Delay */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <Label className="text-zinc-400 text-xs">Delay</Label>
          <span className="text-zinc-500 text-xs">{currentMotion.delayMs || 0}ms</span>
        </div>
        <Slider
          value={[currentMotion.delayMs || 0]}
          onValueChange={([val]) => handleMotionChange('delayMs', val)}
          min={0}
          max={1000}
          step={50}
          className="w-full"
        />
      </div>
      
      {/* Easing */}
      <div>
        <Label className="text-zinc-400 text-xs mb-1.5 block">Easing</Label>
        <Select
          value={currentMotion.easing || 'smooth'}
          onValueChange={(val) => handleMotionChange('easing', val)}
        >
          <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-700">
            {EASING_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value} className="text-white">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Entrance Animation */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-zinc-400 text-xs mb-1.5 block">Fade From</Label>
          <Slider
            value={[(currentMotion.fromOpacity ?? 0) * 100]}
            onValueChange={([val]) => handleMotionChange('fromOpacity', val / 100)}
            min={0}
            max={100}
            step={10}
            className="w-full"
          />
        </div>
        <div>
          <Label className="text-zinc-400 text-xs mb-1.5 block">Slide Y</Label>
          <Slider
            value={[currentMotion.fromY || 30]}
            onValueChange={([val]) => handleMotionChange('fromY', val)}
            min={0}
            max={100}
            step={5}
            className="w-full"
          />
        </div>
      </div>
      
      {/* Replay button */}
      <Button
        variant="outline"
        size="sm"
        onClick={onReplayAnimation}
        className="w-full border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
      >
        <Play className="w-3 h-3 mr-2" />
        Replay Animation
      </Button>
    </div>
  );
};

// Image Panel - Change image
const ImagePanel = ({ blockProps, tokens, onUpdateValue, elementId, onClose }) => {
  const currentValue = getElementValue(blockProps, { id: elementId, bindPath: elementId.split('.').slice(1).join('.') });
  const imageUrl = typeof currentValue === 'object' ? currentValue.url : currentValue;
  const [localUrl, setLocalUrl] = useState(imageUrl || '');

  const handleUrlChange = (url) => {
    setLocalUrl(url);
    if (typeof currentValue === 'object') {
      onUpdateValue?.({ ...currentValue, url });
    } else {
      onUpdateValue?.(url);
    }
  };

  return (
    <div className="space-y-3">
      <PanelHeader title="Change Image" icon={Image} onClose={onClose} />
      
      {/* Image Preview */}
      {localUrl && (
        <div className="aspect-video bg-zinc-800 rounded-lg overflow-hidden">
          <img
            src={localUrl}
            alt="Preview"
            className="w-full h-full object-cover"
            onError={(e) => e.target.style.display = 'none'}
          />
        </div>
      )}
      
      {/* URL Input */}
      <div>
        <Label className="text-zinc-400 text-xs mb-1.5 block">Image URL</Label>
        <Input
          value={localUrl}
          onChange={(e) => handleUrlChange(e.target.value)}
          placeholder="https://..."
          className="bg-zinc-800 border-zinc-700 text-white text-sm"
        />
      </div>
      
      {/* Upload hint */}
      <p className="text-[10px] text-zinc-600 text-center">
        Paste an image URL or use the main Inspector for uploads
      </p>
    </div>
  );
};

// Link Panel - Button destination
const LinkPanel = ({ blockProps, tokens, onUpdateValue, elementId, onClose }) => {
  const currentValue = getElementValue(blockProps, { id: elementId, bindPath: elementId.split('.').slice(1).join('.') });
  const linkData = typeof currentValue === 'object' ? currentValue : { label: currentValue, href: '' };
  
  const [localData, setLocalData] = useState(linkData);

  const handleChange = (key, value) => {
    const updated = { ...localData, [key]: value };
    setLocalData(updated);
    onUpdateValue?.(updated);
  };

  return (
    <div className="space-y-3">
      <PanelHeader title="Link Settings" icon={Link2} onClose={onClose} />
      
      {/* Button Text */}
      <div>
        <Label className="text-zinc-400 text-xs mb-1.5 block">Button Text</Label>
        <Input
          value={localData.label || ''}
          onChange={(e) => handleChange('label', e.target.value)}
          placeholder="Click here"
          className="bg-zinc-800 border-zinc-700 text-white text-sm"
        />
      </div>
      
      {/* Link URL */}
      <div>
        <Label className="text-zinc-400 text-xs mb-1.5 block">Link URL</Label>
        <Input
          value={localData.href || ''}
          onChange={(e) => handleChange('href', e.target.value)}
          placeholder="https://..."
          className="bg-zinc-800 border-zinc-700 text-white text-sm"
        />
      </div>
      
      {/* Open in new tab */}
      <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
        <input
          type="checkbox"
          checked={localData.target === '_blank'}
          onChange={(e) => handleChange('target', e.target.checked ? '_blank' : '_self')}
          className="rounded border-zinc-700 bg-zinc-800"
        />
        Open in new tab
      </label>
    </div>
  );
};

// Content Panel - Card/complex content editor
const ContentPanel = ({ blockProps, tokens, onUpdateValue, elementId, onClose }) => {
  const currentValue = getElementValue(blockProps, { id: elementId, bindPath: elementId.split('.').slice(1).join('.') });
  const contentData = typeof currentValue === 'object' ? currentValue : { title: currentValue };
  
  const [localData, setLocalData] = useState(contentData);

  const handleChange = (key, value) => {
    const updated = { ...localData, [key]: value };
    setLocalData(updated);
    onUpdateValue?.(updated);
  };

  return (
    <div className="space-y-3">
      <PanelHeader title="Edit Content" icon={Type} onClose={onClose} />
      
      {/* Title */}
      {'title' in contentData && (
        <div>
          <Label className="text-zinc-400 text-xs mb-1.5 block">Title</Label>
          <Input
            value={localData.title || ''}
            onChange={(e) => handleChange('title', e.target.value)}
            className="bg-zinc-800 border-zinc-700 text-white text-sm"
          />
        </div>
      )}
      
      {/* Label (alternative to title) */}
      {'label' in contentData && !('title' in contentData) && (
        <div>
          <Label className="text-zinc-400 text-xs mb-1.5 block">Label</Label>
          <Input
            value={localData.label || ''}
            onChange={(e) => handleChange('label', e.target.value)}
            className="bg-zinc-800 border-zinc-700 text-white text-sm"
          />
        </div>
      )}
      
      {/* Description */}
      {'description' in contentData && (
        <div>
          <Label className="text-zinc-400 text-xs mb-1.5 block">Description</Label>
          <Textarea
            value={localData.description || ''}
            onChange={(e) => handleChange('description', e.target.value)}
            className="bg-zinc-800 border-zinc-700 text-white text-sm min-h-[60px] resize-none"
          />
        </div>
      )}
    </div>
  );
};

// Main FloatingPanel component
const FloatingPanel = ({
  panelType,
  elementId,
  elementType,
  elementLabel,
  blockProps,
  tokens,
  onUpdateValue,
  onUpdateStyles,
  onUpdateMotion,
  onReplayAnimation,
  onClose,
  anchorRef
}) => {
  const panelRef = useRef(null);
  
  // Floating UI for panel positioning
  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    middleware: [
      offset(4),
      flip({ fallbackPlacements: ['top-start', 'bottom-end', 'top-end'] }),
      shift({ padding: 8 })
    ],
    whileElementsMounted: autoUpdate
  });

  // Set floating ref callback
  const setFloatingRef = useCallback((node) => {
    refs.setFloating(node);
  }, [refs]);

  // Set anchor reference
  useEffect(() => {
    if (anchorRef?.current) {
      refs.setReference(anchorRef.current);
    }
  }, [anchorRef, refs]);

  // Render the appropriate panel
  const renderPanel = () => {
    const commonProps = {
      blockProps,
      tokens,
      elementId,
      onClose
    };

    switch (panelType) {
      case 'text':
        return <TextPanel {...commonProps} onUpdateValue={onUpdateValue} onUpdateStyles={onUpdateStyles} />;
      case 'design':
        return <DesignPanel {...commonProps} onUpdateStyles={onUpdateStyles} />;
      case 'animate':
        return <AnimatePanel {...commonProps} onUpdateMotion={onUpdateMotion} onReplayAnimation={onReplayAnimation} />;
      case 'image':
        return <ImagePanel {...commonProps} onUpdateValue={onUpdateValue} />;
      case 'link':
        return <LinkPanel {...commonProps} onUpdateValue={onUpdateValue} />;
      case 'content':
        return <ContentPanel {...commonProps} onUpdateValue={onUpdateValue} />;
      default:
        return null;
    }
  };

  return (
    <div
      ref={setFloatingRef}
      style={floatingStyles}
      className="pointer-events-auto"
    >
      <div
        ref={panelRef}
        className="w-64 bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl p-3 mt-2"
        data-testid={`floating-panel-${panelType}`}
      >
        {renderPanel()}
      </div>
    </div>
  );
};

export default FloatingPanel;
